import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaChartLine, FaArrowLeft } from 'react-icons/fa';
import { Clock } from 'lucide-react';

import WatchlistButton from '../../components/watchList/watchlistFollowButton';
import ChartContainer from '../../charts/chartMain/chart';
import InformationBar from './StockDetailsComponents/StockInformationBar';
import SentimentNews from '../../components/sentimentNews/sentimentNews';
import EdgarFilings from '../../components/edgarComponent/edgarComponent';
import StockLogo from '../../components/StockLogo/StockLogoRender';
import StockGroupsPanel from './StockDetailsComponents/StockGroupsPanel';
import CommunityGroupPosts from './StockDetailsComponents/CommunityGroupPosts';
import RelatedPublishedNews from './StockDetailsComponents/RelatedPublishedNews';

import { getMarketStatus } from './StockDetailsComponents/marketutils';

//Action calls 
import { fetchSingleSnapshot } from '../../redux/stockData/marketSnapshot/marketSnapshotActions';
import { fetchPolygonStockData } from '../../redux/polygon/polygonActions';
import { fetchStockSvgThunk } from '../../redux/iconStocks/iconStocksActionThunks';
import { fetchEdgarFilingsData } from '../../redux/stockData/edgar/edgarActions';
import { fetchSentimentData } from '../../redux/stockSentiment/sentimentActions';
import { fetchFinancialsData, fetchTickerDetailsData } from '../../redux/polygon/polygonActions';
import { fetchStockSentimentAggsThunk } from '../../redux/sentimentAggregator/sentimentAggActionThunks';
import { setCurrentSymbol } from '../../redux/stockData/nasdaq/stockDataReducer';
import { setCurrentQuery } from '../../redux/searchBar/searchActions';

// Normalize a symbol from the URL (strip $, @, spaces; uppercase; allow A-Z 0-9 . -)
function normalizeSymbol(raw) {
  const decoded = decodeURIComponent(String(raw || '')).trim();
  const noPrefix = decoded.replace(/^[$@]+/, '');
  const upper = noPrefix.toUpperCase();
  const safe = upper.replace(/[^A-Z0-9.\-]/g, '');
  return safe.slice(0, 12); // hard cap for sanity
}

function StockDetailPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const location = useLocation();
  const { symbol: symbolParam } = useParams();
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const TABS = useMemo(() => ([
    { key: 'news', label: t('stocks.detail.tabs.news') },
    { key: 'community', label: t('stocks.detail.tabs.community') },
    { key: 'filings', label: t('stocks.detail.tabs.filings') },
  ]), [t]);

  // Symbol from URL (authoritative)
  const symbolFromUrl = useMemo(() => normalizeSymbol(symbolParam), [symbolParam]);

  // Redux state
  const { currentSymbol } = useSelector((state) => state.stockData);
  const userId = useSelector((state) => state.auth?.user?.id);
  const marketSnapshot = useSelector((state) => state.marketSnapshot || {});

  // stockData.currentStock and stockData.data.stock never existed -- stockData's
  // own reducer only ever sets `data` to a plain chart-bars array (see
  // stockDataReducer.js), so both paths always resolved null. The real,
  // populated source on this page is marketSnapshot.singleSnapshot.stock.id
  // (MarketSnapshotSuccess.stock: Optional[NasdaqStockSchema] on the backend).
  const stockId = marketSnapshot?.singleSnapshot?.stock?.id || null;

  const openClose = useSelector((state) => state.openClose || {});
  const edgarError = useSelector((state) => state.edgar?.error);
  const sentimentError = useSelector((state) => state.sentiment?.error);

  const [activeTab, setActiveTab] = useState('news');
  const [showAllNews, setShowAllNews] = useState(false);

  // Keep Redux currentSymbol in sync with URL
  useEffect(() => {
    if (symbolFromUrl && symbolFromUrl !== currentSymbol) {
      dispatch(setCurrentSymbol(symbolFromUrl));
      dispatch(setCurrentQuery(symbolFromUrl)); // optional global query sync
    }
  }, [dispatch, symbolFromUrl, currentSymbol]);

  // Effective symbol
  const symbol = symbolFromUrl || currentSymbol || '';

  // Data fetch cycle. fetchEdgarFilingsData is deliberately NOT here --
  // 2026-08-11: 'filings' isn't the default tab ('news' is, see activeTab
  // above), so every page load was paying for a filings fetch most visits
  // never look at. Fetched lazily instead, on first switch to that tab
  // (see the effect below) -- EdgarFilings.jsx is a pure state-reader with
  // no fetch of its own, so this is the only place that populates it.
  const fetchAllStockData = useCallback((sym) => {
    dispatch(fetchTickerDetailsData(sym));
    dispatch(fetchFinancialsData(sym));
    dispatch(fetchSingleSnapshot(sym));
    dispatch(fetchPolygonStockData(sym));
    dispatch(fetchStockSvgThunk(sym));
    dispatch(fetchSentimentData(sym));
    dispatch(fetchStockSentimentAggsThunk({ ticker: sym, timeframe: 'all' }));
  }, [dispatch]);

  useEffect(() => {
    if (!symbol) return;

    // 2026-08-11: this used to be gated behind an unconditional 1.5s
    // setTimeout ("add short delay") with no dependency it was actually
    // waiting on -- confirmed via a real network trace that this alone
    // accounted for a third of this page's 4+ second reported load time,
    // before any of the 8 real fetches below even started. Fetching
    // immediately instead.
    fetchAllStockData(symbol);

    // Periodic refresh every 15 min
    const interval = setInterval(() => {
      setLastRefresh(new Date());
      fetchAllStockData(symbol);
    }, 15 * 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [symbol, fetchAllStockData]);

  // Lazy filings fetch (2026-08-11): fires once per symbol, the first time
  // the user actually opens the Filings tab, instead of on every page load
  // regardless of which tab (if any) they visit.
  useEffect(() => {
    if (activeTab === 'filings' && symbol) {
      dispatch(fetchEdgarFilingsData(symbol));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, symbol]);

  const retryStockData = useCallback(() => {
    if (!symbol) return;
    fetchAllStockData(symbol);
    // fetchEdgarFilingsData isn't in fetchAllStockData (lazy-loaded, see
    // above) -- a manual Retry click is a deliberate action, not an
    // automatic page-load fetch, so it's fine to always include it here
    // regardless of which tab is active.
    dispatch(fetchEdgarFilingsData(symbol));
  }, [symbol, fetchAllStockData, dispatch]);

  // This page previously had zero error surfacing across its whole 8-fetch
  // fan-out -- a rejected thunk just left that section blank/stale with no
  // visible sign anything had failed. Non-blocking: covers the 4 sections
  // most central to the page (price/snapshot, company details, filings,
  // sentiment); logo (iconStocks) and sentiment-aggregate errors are left
  // out as secondary/cosmetic.
  const stockDetailLoadErrors = [
    marketSnapshot.singleError && { key: 'snapshot', label: t('stocks.detail.loadErrors.priceData') },
    openClose.error && { key: 'details', label: t('stocks.detail.loadErrors.companyDetails') },
    edgarError && { key: 'edgar', label: t('stocks.detail.loadErrors.filings') },
    sentimentError && { key: 'sentiment', label: t('stocks.detail.loadErrors.sentiment') },
  ].filter(Boolean);

  // Get market status and company info
  const snapshot = marketSnapshot.singleSnapshot;
  const openCloseData = openClose.data;
  const marketStatus = openCloseData?.market_status;
  const tickerDetails = openClose.tickerDetails?.[0];
  const companyName = snapshot?.stock?.name || tickerDetails?.name || symbol;

  const statusInfo = getMarketStatus(marketStatus, t);

return (
  <div className="flex-1 bg-white text-gray-900 dark:bg-gray-900 dark:text-blue-100 pb-20">
    <main className="p-4 md:p-8 space-y-8 max-w-4xl mx-auto overflow-hidden">

      {/* Slim sticky chrome bar -- the real "hero" identity moment (large
          logo, gradient symbol, price) lives in StockHeader.jsx below;
          this stays unobtrusive once scrolled past it. */}
      <div className="
          flex items-center gap-3
          pb-4 border-b border-blue-200 dark:border-blue-900/30 mb-6
          sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl
        ">
        <button
          onClick={() => window.history.back()}
          aria-label={t('stocks.detail.goBack')}
          className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg border border-blue-200 dark:border-blue-900/50 transition-colors flex-shrink-0"
        >
          <FaArrowLeft className="text-indigo-600 dark:text-indigo-400" />
        </button>

        <StockLogo symbol={symbol} name={companyName} size="w-7 h-7" />

        <div className="flex flex-col gap-2 sm:gap-3 min-w-0 flex-1">
            <span className="truncate text-sm sm:text-base font-semibold text-gray-700 dark:text-gray-300 leading-tight">
              {symbol}
            </span>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <div className={`px-3 py-1 text-sm font-medium rounded-lg border flex items-center gap-2 ${statusInfo.bgColor} ${statusInfo.color}`}>
                  <statusInfo.icon className="w-4 h-4" />
                  {statusInfo.label}
                </div>

                {marketStatus?.serverTime && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Clock className="w-4 h-4" />
                    {new Date(marketStatus.serverTime).toLocaleString()}
                  </div>
                )}

                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  {t('stocks.detail.fifteenMinDelay')}
                </div>
              </div>
          </div>

        <div className="flex space-x-2 shrink-0">
          {userId && (stockId || symbol) && (
            <WatchlistButton
              userId={userId}
              stockId={stockId}
              symbol={symbol}
            />
          )}
        </div>
      </div>

      {/* ── LOAD ERROR NOTICE ─────────────────────────────────────────────
          Non-blocking: whatever sections did load still render below. */}
      {stockDetailLoadErrors.length > 0 && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/20 text-sm text-amber-800 dark:text-amber-300">
          <span>
            {t('stocks.detail.loadErrorNotice', {
              items: stockDetailLoadErrors.map((e) => e.label).join(', '),
              symbol,
            })}
          </span>
          <button
            type="button"
            onClick={retryStockData}
            className="flex-shrink-0 px-3 py-1 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold transition-colors"
          >
            {t('stocks.common.retry')}
          </button>
        </div>
      )}

      <section>
        <ChartContainer />
      </section>

      {/* Groups are the app's primary revenue surface (creator-run,
          subscription-backed communities) -- placed as the second major
          section, right after the primary chart-viewing moment and ahead
          of the reference-data tabs below, so it's impossible to miss
          without displacing the chart itself from the top of the page. */}
      {symbol && (
        <section>
          <StockGroupsPanel symbol={symbol} />
        </section>
      )}

      <section>
        <InformationBar />
      </section>

      <section>
        <div className="flex items-center justify-start gap-2 mb-4 flex-wrap">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`px-5 py-2 rounded-full font-bold border transition ${
                activeTab === tab.key
                  ? 'bg-blue-500 text-white border-blue-400 shadow'
                  : 'bg-gray-100 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-slate-800 dark:text-blue-200 dark:border-blue-400/20 dark:hover:bg-blue-600/30'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="rounded-2xl bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-900/50 shadow-lg p-4 min-h-[300px]">
          {activeTab === 'news' && (
            <>
              <SentimentNews
                showAll={showAllNews}
                onToggleShowAll={() => setShowAllNews(!showAllNews)}
              />
              <RelatedPublishedNews symbol={symbol} />
            </>
          )}

          {activeTab === 'community' && <CommunityGroupPosts symbol={symbol} />}

          {activeTab === 'filings' && <EdgarFilings />}
        </div>
      </section>
    </main>
  </div>
);

}

export default StockDetailPage;